'use client'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { english } from 'flatpickr/dist/l10n/default'
import { Russian } from 'flatpickr/dist/l10n/ru'
import { SubmitHandler } from 'react-hook-form'
import Flatpickr from 'react-flatpickr'
import classNames from 'classnames'
import moment from 'moment'

import { Form, Popup, Button, TextInput, SearchInput } from '@/components'
import {
  TLang,
  IOption,
  ILocationData,
  IErrorResponse,
  TStartReservationSchema,
  IStartReservationPayload,
  IStartReservationResponse,
} from '@/types'
import {
  useHookForm,
  useLanguages,
  useOnClickOutside,
  useOnResize,
} from '@/hooks'
import {
  setStorageValue,
  pickupAndReturn,
  selectVehiclePath,
  startReservationForm,
  viewModifyCancelPath,
  joinArray,
  getStorageValue,
} from '@/utils'
import { DATE_FORMAT, LANG_KEY, START_KEY } from '@/constants'
import { useStartReservation } from '@/apis'
import { Link } from '@/i18n'

import { driverAges, times } from './constants'

const MIN_DATE = moment().add(1, 'day').toDate()

interface IReserveFormProps {
  differentLocation?: boolean
  pickupDetails?: Pick<
    TStartReservationSchema,
    | 'pickUpStationCode'
    | 'pickUpLatitude'
    | 'pickUpLocation'
    | 'pickUpLongitude'
  >
}

export const ReserveForm: React.FC<IReserveFormProps> = ({
  pickupDetails,
  differentLocation,
}) => {
  const { t, isRussian } = useLanguages()
  const calendarRef = useRef<Flatpickr>(null)
  const [isMobile, setIsMobile] = useState(false)
  const openedCalendar = useRef<'pickUp' | 'return'>()
  const [errorMessage, setErrorMessage] = useState<string>()
  // @ts-ignore
  const returnRef = useRef<HTMLDivElement>(undefined)
  // @ts-ignore
  const pickupRef = useRef<HTMLDivElement>(undefined)
  const {
    handler: {
      trigger,
      register,
      setValue,
      getValues,
      handleSubmit,
      formState: { errors },
    },
  } = useHookForm(startReservationForm, {
    defaultValues: {
      driverAge: '25',
      pickUpTime: '10:00',
      returnTime: '10:00',
      pickUpDate: MIN_DATE,
      returnDate: moment(MIN_DATE).add(3, 'day').toDate(),
    },
  })

  const { mutateAsync: startReservation } = useStartReservation()

  const onResize = useCallback(() => {
    setIsMobile(window?.innerWidth < 991)
  }, [])

  useOnResize(onResize)

  const onToggleLocation = useCallback(() => {
    const differentLocation = getValues('differentLocation') ? 0 : 1
    setValue('differentLocation', differentLocation)
    trigger('differentLocation')
    if (!differentLocation) {
      setValue('dropOffLatitude', undefined)
      setValue('dropOffLocation', undefined)
      setValue('dropOffLongitude', undefined)
      setValue('dropOffStationCode', undefined)
    }
  }, [setValue, trigger, getValues])

  const onToggleCoupon = useCallback(() => {
    const hasCode = getValues('hasCode') ? 0 : 1
    setValue('hasCode', hasCode)
    trigger('hasCode')
    if (!hasCode) {
      setValue('promotionCode', undefined)
    }
  }, [trigger, setValue, getValues])

  const onSelect = useCallback(
    (field: 'pickUpTime' | 'returnTime' | 'driverAge') => (option: IOption) => {
      setValue(field, option.value)
      trigger(field)
    },
    [setValue, trigger],
  )

  useEffect(() => {
    if (differentLocation !== undefined) {
      setValue('differentLocation', differentLocation ? 1 : 0)
      trigger('differentLocation')
    }
  }, [differentLocation, setValue, trigger])

  useEffect(() => {
    if (!!pickupDetails) {
      Object.keys(pickupDetails).map(key => {
        // @ts-ignore
        setValue(key, pickupDetails[key])
        // @ts-ignore
        trigger(key)
      })
    }
  }, [pickupDetails, setValue, trigger])

  const onSubmit: SubmitHandler<TStartReservationSchema> = useCallback(
    async data => {
      setErrorMessage(undefined)
      try {
        const {
          hasCode,
          driverAge,
          pickUpDate,
          pickUpStationCode,
          differentLocation,
          dropOffStationCode,
          returnDate,
          ...rest
        } = data

        const payload: IStartReservationPayload = {
          ...rest,
          driverAge: driverAge,
          // @ts-ignore
          differentLocation: `${differentLocation}`,
          returnDate: moment(returnDate).format(DATE_FORMAT),
          pickUpDate: moment(pickUpDate).format(DATE_FORMAT),
          pickUpStationCode: !pickUpStationCode ? null : pickUpStationCode,
          dropOffStationCode: !dropOffStationCode ? null : dropOffStationCode,
        }
        const response = await startReservation(payload)

        const _data = {
          ...response.data,
          parameters: data,
          startedAt: new Date(),
        }
        if (!response.data?.isLocationPage) {
          const data = response.data.data as IStartReservationResponse
          if (!data.vehicles?.some?.(_v => _v.status === 'Available')) {
            setErrorMessage(t('popupCarsNotAvailable'))
            return
          }
        }
        const isEvn = [
          data.pickUpLocation.toLowerCase(),
          data?.dropOffLocation?.toLowerCase(),
        ].some(_v => _v?.includes('yerevan'))
        const _opts = !isEvn ? { locale: 'en' as TLang } : {}
        setStorageValue(START_KEY, _data, true)
        if (Object.keys(_opts).length) {
          setStorageValue(LANG_KEY, 'en')
        }
        const lang = getStorageValue(LANG_KEY)
        const timer = setTimeout(() => {
          window.location.replace(
            joinArray(
              '',
              lang === 'en' ? undefined : lang,
              response.data?.isLocationPage
                ? pickupAndReturn
                : selectVehiclePath,
            ),
          )
          clearTimeout(timer)
        }, 200)
      } catch (err: any) {
        const error: IErrorResponse = err
        setErrorMessage(error?.response?.data?.message)
      }
    },
    [startReservation, t],
  )

  const onLocationSelect = useCallback(
    (type: 'dropOff' | 'pickUp') => (location?: ILocationData) => {
      setValue(`${type}Location`, location?.name ?? '')
      setValue(`${type}StationCode`, location?.code ?? '')
      setValue(`${type}Latitude`, `${location?.latitude ?? ''}`)
      setValue(`${type}Longitude`, `${location?.longitude ?? ''}`)
      trigger(`${type}StationCode`)
      trigger(`${type}Longitude`)
      trigger(`${type}Latitude`)
      trigger(`${type}Location`)
    },
    [setValue, trigger],
  )

  const onRangeChange = useCallback(
    (dates: Date[]) => {
      if (isMobile) {
        const isPickup = openedCalendar.current === 'pickUp'
        setValue(isPickup ? 'pickUpDate' : 'returnDate', dates[0])
        if (isPickup) {
          setValue('returnDate', moment(dates[0]).add(3, 'day').toDate())
        }
      } else {
        const isEqual = moment(moment(dates[0]).format(DATE_FORMAT)).isSame(
          moment(getValues('pickUpDate')).format(DATE_FORMAT),
        )
        setValue('pickUpDate', dates[0])
        setValue(
          'returnDate',
          isEqual ? dates[1] : moment(dates[0]).add(3, 'day').toDate(),
        )
      }
      trigger('pickUpDate')
      trigger('returnDate')
      calendarRef.current?.flatpickr?.close()
    },
    [getValues, setValue, trigger, isMobile],
  )

  useEffect(() => {
    calendarRef.current?.flatpickr.setDate(MIN_DATE)
    setIsMobile(window?.innerWidth < 991)
  }, [])

  const options = useMemo(
    () => ({
      mode: 'range',
      showMonths: 1,
      minDate: MIN_DATE,
    }),
    [],
  )

  const onOpenCalendar = useCallback(
    (type: 'pickUp' | 'return') => () => {
      const isPickup = type === 'pickUp'
      openedCalendar.current = type
      calendarRef?.current?.flatpickr.set(
        'locale',
        isRussian ? Russian : english,
      )
      calendarRef?.current?.flatpickr.set('showMonths', isMobile ? 1 : 2)
      calendarRef?.current?.flatpickr?.set(
        'monthSelectorType',
        isMobile ? 'dropdown' : 'static',
      )
      calendarRef?.current?.flatpickr.set(
        'mode',
        isMobile || isPickup ? 'single' : 'range',
      )
      calendarRef?.current?.flatpickr.set(
        'minDate',
        isPickup ? MIN_DATE : getValues('pickUpDate'),
      )

      !isPickup &&
        calendarRef?.current?.flatpickr.setDate([getValues('pickUpDate')])

      calendarRef?.current?.flatpickr.open(
        undefined,
        isPickup ? pickupRef.current : returnRef.current,
      )
    },
    [getValues, isMobile, isRussian],
  )

  return (
    <div className="column small-12">
      <div className="reservation_top flex-container align-justify align-middle">
        <div className="flex-container">
          <h2 className="white-color">{t('startAReservation')}</h2>
          <span className="text">
            <sup>*</sup>
            {t('indicatesRequiredField')}
          </span>
        </div>
        <Link href={viewModifyCancelPath} className="yellow-color">
          <span>{t('viewModifyCancel')}</span>
        </Link>
      </div>
      <Form
        onSubmit={handleSubmit(onSubmit)}
        className="reservation_form flex-container flex-row-wrap"
      >
        <input style={{ visibility: 'hidden', height: 0 }} />
        {!!errorMessage && <div className="bg-yellow">{errorMessage}</div>}
        <div className="small-12">
          <SearchInput
            required
            name="pickUpLocation"
            className="autoComplete field"
            value={getValues('pickUpLocation')}
            label={t('pickUpLabel')}
            placeholder={t('searchPlaceholder')}
            onSelect={onLocationSelect('pickUp')}
            error={errors?.pickUpLocation?.message}
          >
            {!differentLocation && (
              <Button
                type="button"
                onClick={onToggleLocation}
                aria-label={t('differentReturn')}
                className={classNames('add_return icon--plus', {
                  active: !!getValues('differentLocation'),
                })}
              >
                <span> {t('differentReturn')} </span>
              </Button>
            )}
          </SearchInput>
        </div>
        <div
          className={classNames('small-12', {
            'hidden-field': !getValues('differentLocation'),
          })}
        >
          <SearchInput
            name="dropOffLocation"
            className="autoComplete field"
            labelClassName="pickup-location"
            value={getValues('dropOffLocation')}
            label={t('returnLabel')}
            onSelect={onLocationSelect('dropOff')}
            error={errors.dropOffLocation?.message}
            required={!!getValues('differentLocation')}
          />
        </div>
        <div className="flex-container flex-row-wrap column-full custom-wrap">
          <div className="small-5_5 medium-3 large-2">
            <div className="field date-picker" ref={pickupRef}>
              <TextInput
                readOnly
                name={'pickUpDate'}
                label={t('pickUpDate')}
                onClick={onOpenCalendar('pickUp')}
                value={moment(getValues('pickUpDate')).format(DATE_FORMAT)}
              />
            </div>
          </div>
          <div className="small-5_5 medium-3 large-2">
            <Popup
              required
              options={times}
              className="field"
              name="pickUpTime"
              label={t('time')}
              optionClassName="time-picker"
              value={getValues('pickUpTime')}
              onChange={onSelect('pickUpTime')}
            />
          </div>
          <div className="small-5_5 medium-3 large-2">
            <div className="field date-picker" ref={returnRef}>
              <TextInput
                readOnly
                name={'returnDate'}
                label={t('returnDate')}
                onClick={onOpenCalendar('return')}
                value={moment(getValues('returnDate')).format(DATE_FORMAT)}
              />
            </div>
          </div>
          <div className="small-5_5 medium-3 large-2">
            <Popup
              required
              options={times}
              name="returnTime"
              className="field"
              label={t('time')}
              optionClassName="time-picker"
              value={getValues('returnTime')}
              onChange={onSelect('returnTime')}
            />
          </div>
          <div className="small-5_5 medium-3 large-2">
            <Popup
              required
              name="driverAge"
              className="field"
              options={driverAges}
              value={getValues('driverAge')}
              onChange={onSelect('driverAge')}
              label={t('driverAge')}
            />
          </div>
          <TextInput
            className={classNames('field column-part', {
              active: !!getValues('hasCode'),
            })}
            label={t('couponCode')}
            {...register('promotionCode')}
          />
          <Flatpickr
            hidden
            // @ts-ignore
            options={options}
            ref={calendarRef}
            onChange={onRangeChange}
          />
          <Button type="submit" className="reservation_form_submit">
            <span>{t('go')}</span>
          </Button>
        </div>
        <div className="flex-container">
          <div
            onClick={onToggleCoupon}
            className={classNames('discount_btn icon--plus', {
              active: !!getValues('hasCode'),
            })}
          >
            <span>{t('discountCodes')}</span>
          </div>
          <Tooltip />
        </div>
      </Form>
    </div>
  )
}

const Tooltip: React.FC = () => {
  const { t } = useLanguages()
  const ref = useRef<HTMLDivElement>(null)
  const [isOpen, setOpen] = useState(false)

  const onToggle = useCallback(() => {
    setOpen(prev => !prev)
  }, [])

  useOnClickOutside(ref, isOpen, onToggle)

  return (
    <span className="tooltips__info-icon" onClick={onToggle}>
      <div
        ref={ref}
        className={classNames('tooltip', {
          open: isOpen,
        })}
      >
        <p>{t('lorem')}</p>
        <i className="icon--close"></i>
      </div>
    </span>
  )
}
